import { useState } from 'react';
import type { ReactNode } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { LocaleProvider } from '@/shared/lib/i18n/locale-context';
import { ThemeModeProvider } from '@/shared/config/theme/theme-mode-context';

/**
 * ⚠️ Never retry an answer the server already gave definitively.
 *
 *   401 — handled by the refresh interceptor, not by repeating the request.
 *   403 — retrying turns one refusal into four identical entries in the audit log (§12.7),
 *         noise that reads like an attack.
 *   404 — a permanent answer. Observed in the browser: a misrouted request produced six
 *         404s where one would have shown the bug faster.
 *   429 — §12.4's limiter counts every attempt, so a retry extends the caller's own lockout.
 *
 * Exported, and deliberately a plain function rather than a closure inside the component:
 * a policy that can only be exercised by rendering the whole panel and provoking four
 * statuses through the network is a policy nobody checks.
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (error instanceof AxiosError) {
    const status = error.response?.status;
    if (status === 401 || status === 403 || status === 404 || status === 429) {
      return false;
    }
  }

  return failureCount < 2;
}

/**
 * §5.3: all server state flows through TanStack Query. Nothing is mirrored into
 * `useState` or a global store.
 */
export function Providers({ children }: { children: ReactNode }) {
  // Created in state, not at module scope: a module-level client is shared across every
  // test and every hot reload, so one test's cache leaks into the next.
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // An operations panel is read repeatedly; 30 s keeps a tab switch from
            // refetching everything while staying fresh enough for a review queue.
            staleTime: 30_000,
            refetchOnWindowFocus: true,

            retry: shouldRetryQuery,
          },
          mutations: {
            // Mutations are never retried automatically. Several of them are not
            // idempotent without a key — casting a committee vote, approving a volunteer —
            // and a silent retry would record the act twice.
            retry: false,
          },
        },
      }),
  );

  return (
    <QueryClientProvider client={queryClient}>
      <LocaleProvider>
        <ThemeModeProvider>{children}</ThemeModeProvider>
      </LocaleProvider>
    </QueryClientProvider>
  );
}
